import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ArrowRight, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO = "https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Partners() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    import("@/lib/api").then(({ default: api }) => api.get("/partners/public").then((r) => setPartners(r.data)).catch(() => {}));
  }, []);

  const factors = ["Property Type", "Loan Purpose", "Geography", "Borrower Qualifications", "Loan Size", "Deal Structure"];

  return (
    <div>
      <section className="relative border-b border-edge overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/95 to-obsidian/70" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="eyebrow mb-3">Capital Partners</div>
          <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl">Multiple capital sources. One point of contact.</h1>
          <p className="mt-5 text-base sm:text-lg text-dim max-w-2xl leading-relaxed">
            BullsEye Capital works with lending and capital partners that serve real estate investors across
            multiple strategies and markets.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="card-rich p-7 sm:p-10">
          <h2 className="h-display text-2xl sm:text-3xl text-white">Financing availability varies.</h2>
          <p className="mt-3 text-dim max-w-2xl">Available financing depends on a number of factors, including:</p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {factors.map((f) => (
              <div key={f} className="rounded-lg border border-edge bg-surface/50 px-4 py-3 text-sm text-white/85 transition-colors hover:border-gold/40 hover:text-white">{f}</div>
            ))}
          </div>
        </div>

        {partners.length > 0 && (
          <div className="mt-12">
            <div className="eyebrow mb-5">Our Partners</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {partners.map((p) => (
                <div key={p.id} className="card-rich p-6" data-testid={`partner-card-${p.id}`}>
                  {p.logo_url ? <img src={p.logo_url} alt={p.name} className="h-10 object-contain" /> : <div className="w-11 h-11 icon-chip"><Building2 className="w-5 h-5 text-gold" /></div>}
                  <h3 className="mt-4 text-lg font-bold text-white">{p.name}</h3>
                  {p.description && <p className="mt-2 text-sm text-dim">{p.description}</p>}
                  {p.products && <p className="mt-3 text-xs text-faint"><span className="text-gold">Products:</span> {p.products}</p>}
                  {p.states && <p className="text-xs text-faint"><span className="text-gold">States:</span> {p.states}</p>}
                  {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-gold"><Globe className="w-4 h-4" /> Visit</a>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 rounded-2xl border border-gold/30 bg-gold/5 p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="h-display text-2xl sm:text-3xl text-white">Have clients or deals that need capital?</h3>
            <p className="mt-2 text-dim">Brokers, wholesalers, agents, acquisition companies, investment groups, and title companies.</p>
          </div>
          <Button onClick={() => navigate("/become-a-partner")} className="btn-gold rounded-full h-14 px-8 gap-2 shrink-0" data-testid="become-partner-cta">
            Become a Partner <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}
